import { createAction } from '@reduxjs/toolkit';

import { AppDispatch } from 'bootstrap.store';

import {
  CreateAsyncThunkActionResult,
  PrepareAsyncAction,
  PrepareAsyncThunkAction,
} from './createAsyncThunkAction.types';

/**
 * Creates and manages three redux actions for asynchronous operations. These
 * are `pending`, `fulfilled`, and `rejected`. The actions can either be
 * dispatched one-by-one for fine grained control. Or the thunk (bare function)
 * can be dispatched and awaited, which in turn will dispatch `pending` and
 * `fulfilled`/`rejected` when the promise executor functions in the meta are
 * invoked.
 *
 * @example
 *   ```
 *   // Declaration
 *   const getFoo = createAsyncThunkAction<number, string, boolean>('app/getFoo');
 *
 *   // Consumption (e.g. in component)
 *   const name = await dispatch(getFoo(42)); // Returns 'Bob'
 *
 *   // Consumption (e.g. in middleware)
 *   action.meta.resolve('Bob'); // Dispatches fulfilled
 *   action.meta.reject(false); // Dispatches rejected
 *   ```;
 *
 * @example
 *   ```
 *   // Declaration
 *   const getFoo = createAsyncThunkAction<number, string, boolean>('app/getFoo');
 *
 *   // Consumption
 *   dispatch(getFoo.pending(41));
 *   dispatch(getFoo.fulfilled('Alice'));
 *   dispatch(getFoo.rejected(false));
 *   ```;
 *
 * @template TPending The pending payload.
 * @template TFulfilled The fulfilled payload.
 * @template TRejected The rejected payload.
 * @param typePrefix The action prefix.
 */
export function createAsyncThunkAction<
  TPending = void,
  TFulfilled = void,
  TRejected = void,
>(
  typePrefix: string,
): CreateAsyncThunkActionResult<TPending, TFulfilled, TRejected> {
  const pending = createAction<PrepareAsyncAction<TPending>>(
    `${typePrefix}/pending`,
    (payload) => ({
      meta: { requestStatus: 'pending' },
      payload,
    }),
  );
  const fulfilled = createAction<PrepareAsyncAction<TFulfilled>>(
    `${typePrefix}/fulfilled`,
    (payload) => ({
      meta: { requestStatus: 'fulfilled' },
      payload,
    }),
  );
  const rejected = createAction<PrepareAsyncAction<TRejected>>(
    `${typePrefix}/rejected`,
    (payload) => ({
      meta: { requestStatus: 'rejected' },
      payload,
    }),
  );

  const thunk = createAction<
    PrepareAsyncThunkAction<TPending, TFulfilled, TRejected>
  >(`${typePrefix}/pending`, (payload, resolve, reject) => ({
    meta: { requestStatus: 'pending', resolve, reject },
    payload,
  }));

  return Object.assign(
    (payload: TPending) => {
      return async (dispatch: AppDispatch) => {
        try {
          const result = await new Promise<TFulfilled>((resolve, reject) => {
            dispatch(thunk(payload, resolve, reject));
          });
          return dispatch(fulfilled(result));
        } catch (error: any) {
          return dispatch(rejected(error));
        }
      };
    },
    {
      thunk,
      pending,
      fulfilled,
      rejected,
    },
  );
}
